import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Bell, CheckCircle2, CreditCard, Diamond, FileText, HeartHandshake, HelpCircle, MailOpen, Sparkles, Users, XCircle } from "lucide-react";

import { api } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { EmptyState, ErrorState, Loading, PageHeader } from "@/components/ui";

const ICONS = {
  registration_success: Sparkles, request_received: MailOpen, request_accepted: HeartHandshake, connection_created: Users,
  request_declined: XCircle, daily_drop: Sparkles, verification_payment: CreditCard, verification_submitted: FileText,
  verification_verified: CheckCircle2, verification_rejected: AlertCircle, verification_more_info_needed: HelpCircle,
};

export default function Notifications() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["notifications"], queryFn: () => api("/notifications") });
  const refreshAll = () => ["notifications", "unread"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const open = async (n) => {
    if (!n.read) {
      await api(`/notifications/${n.id}/read`, { method: "POST" }).catch(() => {});
      refreshAll();
    }
    if (n.type === "request_received") navigate("/requests");
    else if (n.type === "request_accepted" || n.type === "connection_created") navigate("/connections");
    else if (n.type === "registration_success") navigate("/profile");
    else if (n.type === "daily_drop") navigate("/discover");
    else if (n.type.startsWith("verification")) navigate("/verification");
    else if (n.type.startsWith("membership")) navigate("/membership");
  };
  const markAll = async () => {
    await api("/notifications/read-all", { method: "POST" }).catch(() => {});
    refreshAll();
  };

  return (
    <div data-testid="notifications-screen" className="mx-auto max-w-3xl">
      <PageHeader title="Notifications" subtitle={data ? `${data.unread_count} unread` : undefined} right={data?.unread_count ? (
        <button data-testid="notifications-mark-all-read" onClick={markAll} className="h-10 rounded-full px-4 text-sm font-bold text-primary hover:bg-[#F6E3E4]">Mark all read</button>
      ) : null} />
      {isLoading ? <Loading /> : isError ? <ErrorState testId="notifications-error" message={error.message} onRetry={() => refetch()} /> : !data?.notifications.length ? (
        <EmptyState testId="notifications-empty" icon={Bell} title="You are all caught up." />
      ) : (
        <ul className="flex flex-col gap-2">
          {data.notifications.map((n) => {
            const Icon = ICONS[n.type] ?? (n.type.startsWith("membership") ? Diamond : Bell);
            return (
              <li key={n.id}>
                <button data-testid={`notification-item-${n.id}`} onClick={() => open(n)} className={`flex w-full items-start gap-3 rounded-[20px] border p-4 text-left transition-colors hover:border-[#D48D93] ${n.read ? "border-border bg-white" : "border-transparent bg-[#F4EBE6]"}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#F6E3E4]"><Icon className="h-[22px] w-[22px] text-primary" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[15px] font-bold">{n.title}</span>
                    <span className="mt-0.5 block text-sm leading-5">{n.body}</span>
                    <span className="mt-1 block text-xs text-muted-foreground">{timeAgo(n.created_at)}</span>
                  </span>
                  {!n.read ? <span className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-primary" data-testid={`notification-unread-dot-${n.id}`} /> : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
